// Munkatárs — jobb oldali beúszó FormSheet, KÉT módban (a billing-profil-minta: külön create/edit instance).
// CREATE (`employee == null`, TISZTA GLOBÁLIS IdP-provisioning): e-mail + név SZERKESZTHETŐ (az e-mail a kulcs;
// ha már létezik, az Odin csak a core-t upsertálja — nem írja felül [A döntés]) + munkaviszony-adat. NINCS
// szerep-választó: az app-tagság/szerep a Jogosultságok-oldal dolga (IdP-modell — az identity GLOBÁLIS).
// EDIT (`employee != null`): az identity-mag (e-mail/név) is SZERKESZTHETŐ — az Odin `PATCH /v1/admin/identities/:id`
// írja (e-mail-ütközés → az e-mail mezőre vezetett hiba) — + a munkaviszony-adat (positionId + HR-dátumok). A
// support-csapat-tagságokat MÁR NEM ez kezeli (a support-csapat-szerkesztő). A validálás forrás-igazsága a
// @atlas/contracts (create/update body-séma), a resolver a features/employees/form.ts segédein át.
//
// (user-döntés): a DISCORD-ID és az ASANA PAT KIVEZETVE — mindkettő SZEMÉLYES integrációs adat, amit
// a felhasználó a saját „Profilom" oldalán állít (egy admin-űrlapon a titok-mező fölösleges expozíció volt).
// HELYETTE a FELVÉTELNÉL egy opcionális KEZDŐ JELSZÓ mező áll: enélkül a felvett kolléga csak a köszöntő-levél
// linkjével tudott belépni. A SZERKESZTÉSNÉL SZÁNDÉKOSAN NINCS jelszó-mező: a csere self-service („Profilom")
// vagy elfelejtett-jelszó úton megy — így az admin nem tud némán jelszót cserélni más nevében.
import { EMPLOYER_KEYS, EMPLOYMENT_TYPES, type EmployeeRowSchema } from '@atlas/contracts';
import { useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { type z } from 'zod';

import {
  DateField,
  SearchableSelectField,
  SelectField,
  SwitchField,
  TextField,
} from '@/components/shared/form-fields';
import { FormSheet } from '@/components/shared/form-sheet';
import { PasswordPolicyNote } from '@/components/shared/password-policy-note';
import { PersonChildrenField } from '@/components/shared/person-children-field';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { employeeKeys } from '@/features/employees/api';
import {
  buildEmployeeCreateResolver,
  buildEmployeeResolver,
  EMPTY_EMPLOYEE_FORM,
  toEmployeeBody,
  toEmployeeCreateBody,
  type EmployeeFormValues,
} from '@/features/employees/form';
import { api } from '@/lib/api';
import { formatName } from '@/lib/format-name';
import { useEntityMutation } from '@/lib/use-entity-mutation';

type EmployeeRow = z.infer<typeof EmployeeRowSchema>;

export interface EmployeeFormSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** `null` → LÉTREHOZÁS (create); egyébként a szerkesztendő munkatárs (OCC `version` + előtöltés). */
  employee: EmployeeRow | null;
}

export function EmployeeFormSheet({
  open,
  onOpenChange,
  employee,
}: EmployeeFormSheetProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const lang = i18n.language.toLowerCase().startsWith('en') ? ('en' as const) : ('hu' as const);
  const isCreate = employee === null;

  // Szerkesztő-opciók (pozíciók) — locale-tudatos név (a lang a cache-kulcs része).
  const refsQ = api.employeeRefs.useQuery(['employee-refs', lang], { query: { lang } });
  const refs = refsQ.data?.status === 200 ? refsQ.data.body : undefined;

  const form = useForm<EmployeeFormValues>({
    // A resolver a mód szerint (create/edit ugyanaz az identity-mag + munkaviszony); instance-onként stabil.
    resolver: isCreate ? buildEmployeeCreateResolver(t) : buildEmployeeResolver(t),
    mode: 'onTouched', // validáció már mentés előtt: a mezőből kilépve (blur), utána élőben
    defaultValues: EMPTY_EMPLOYEE_FORM,
  });
  const { reset, handleSubmit, setError } = form;
  // a próbaidő-dátumok csak bekapcsolt kapcsolóval látszanak (#111)
  const probation = useWatch({ control: form.control, name: 'probation' });
  /**
   * A SZERVER jelszó-policy kódjai (`too_short`, …) a kezdő-jelszó mező alatt. Külön állapot (nem RHF-hiba),
   * mert a lista több sértést hordoz egyszerre, és a lokalizálás a kódokból történik — nyers kód SOHA nem
   * jelenik meg (a `PasswordPolicyNote` ismeretlen kódra generikus szöveget ad).
   */
  const [passwordCodes, setPasswordCodes] = useState<string[]>([]);

  useEffect(() => {
    if (employee) {
      reset({
        ...EMPTY_EMPLOYEE_FORM,
        email: employee.email ?? '',
        givenName: employee.givenName ?? '',
        familyName: employee.familyName ?? '',
        // Becenév előtöltés a sorból (az Odin listIdentities projektálja). A mentés dirty-alapon véd is: az
        // érintetlen mező nem megy, így egy másik mező mentése sosem nulláz némán meglévő becenevet.
        nickname: employee.nickname ?? '',
        phone: employee.phone ?? '',
        // A beosztás KÖTELEZŐ: a meglévő adat lehet null → üresen (placeholderrel) indul, a user kötelezően választ.
        positionId: employee.positionId ?? '',
        // A HR-dátumok NAP-pontos stringként jönnek a szerződésből (`YYYY-MM-DD`) → közvetlenül a mezőbe.
        birthDate: employee.birthDate ?? '',
        employmentStartDate: employee.employmentStartDate ?? '',
        employmentEndDate: employee.employmentEndDate ?? '',
        nameDay: employee.nameDay ?? '',
        children: employee.children.map((c) => ({ ...c })),
        employmentType: employee.employmentType,
        employerKey: employee.employerKey,
        probation: employee.probation,
        probationStartDate: employee.probationStartDate ?? '',
        probationEndDate: employee.probationEndDate ?? '',
      });
    } else {
      reset(EMPTY_EMPLOYEE_FORM);
    }
  }, [employee, reset]);

  const mutation = useEntityMutation<EmployeeFormValues>({
    mutationFn: (values) =>
      isCreate
        ? api.employeeCreate.mutation({ body: toEmployeeCreateBody(values) })
        : api.employeeUpdate.mutation({
            params: { id: employee.id },
            // A becenevet CSAK dirty esetén küldjük — így egy másik mező mentése nem nullázza némán a meglévő
            // becenevet (az Odin read még nem projektálja → az előtöltés üres lehet).
            body: toEmployeeBody(values, {
              includeNickname: form.formState.dirtyFields.nickname === true,
            }),
            headers: { 'if-match': String(employee.version) },
          }),
    invalidateKeys: [employeeKeys.lists()],
    onFieldErrors: (field, messages) => {
      // Az e-mail-ütközést (a szerver az egyetlen forrás — a kliens nem tudja validálni) LOKALIZÁLT üzenettel
      // vezetjük az e-mail mezőre (a nyers szerver-szöveg helyett), a többi mező-hiba a szerver üzenetét kapja.
      if (field === 'email') {
        setError('email', { message: t('validation:employees.emailConflict') });
        return;
      }
      // A jelszó-policy sértései GÉPI KÓDOK (a szerver az autoritás) — a mező alatti policy-blokk fordítja.
      if (field === 'password') {
        setPasswordCodes(messages);
        return;
      }
      if (field in EMPTY_EMPLOYEE_FORM) {
        setError(field as keyof EmployeeFormValues, { message: messages.join(' ') });
      }
    },
    messages: {
      success: isCreate ? t('toast:employees.created') : t('toast:employees.updated'),
      // Create-nél a 409 = „már munkatárs"; edit-nél = OCC-ütközés. (Külön instance → külön üzenet.)
      conflict: isCreate ? t('toast:employees.alreadyEmployee') : t('toast:employees.conflict'),
      forbidden: t('toast:employees.forbidden'),
      validation: t('toast:employees.validation'),
      error: isCreate ? t('toast:employees.createError') : t('toast:employees.error'),
    },
  });

  const onSubmit = handleSubmit((values) => {
    setPasswordCodes([]);
    mutation.mutate(values, {
      onSuccess: (res) => {
        // MATCH-ág: a megadott kezdő jelszó NEM íródott (az e-mailhez már tartozott identity) — ezt KI KELL
        // írni, különben az admin tévesen hinné, hogy az általa beírt jelszóval lép be a kolléga.
        const passwordSet = (res.body as { passwordSet?: boolean } | undefined)?.passwordSet;
        if (isCreate && values.password !== '' && passwordSet === false) {
          toast.warning(t('toast:employees.passwordNotSet'));
        }
        onOpenChange(false);
      },
    });
  });

  // A beosztás KÖTELEZŐ (nincs „nincs beosztás" opció) — csak a valós pozíciók; a required + placeholder kényszerít.
  //
  // A CÍMKÉBEN OTT A CSAPAT, és ez nem dísz: a beosztás-név csak CSAPATON BELÜL egyedi (a fa szerint egy
  // szerep — pl. „Software Engineer" — több csapatban is élhet), tehát a puszta név mellett két azonos
  // címke állna egymás mellett, és a választás találgatás lenne. Csapat nélküli (árva) sornál a csupasz
  // név marad, nem egy üres elválasztó. A kereső a CÍMKÉRE illeszt, így a csapat-névre is lehet szűrni.
  const positionOptions = (refs?.positions ?? []).map((p) => ({
    value: p.id,
    label: p.teamName
      ? t('employees.fields.positionOptionWithTeam', { name: p.name, team: p.teamName })
      : p.name,
  }));

  // Az EDIT-fejléc alcíme: a szerkesztendő munkatárs neve (a lista formatName-konvenciója szerint).
  const identityName = employee
    ? formatName(
        {
          givenName: employee.givenName,
          familyName: employee.familyName,
          displayName: employee.displayName ?? employee.email,
        },
        i18n.language,
      )
    : '';

  return (
    <FormSheet
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          reset(EMPTY_EMPLOYEE_FORM);
          setPasswordCodes([]);
        }
        onOpenChange(o);
      }}
      title={isCreate ? t('employees.createTitle') : t('employees.editTitle')}
      description={isCreate ? t('employees.createDesc') : identityName || undefined}
      onSubmit={onSubmit}
      busy={mutation.isPending}
      footer={
        <>
          <Button
            variant="outline"
            type="button"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            {t('employees.cancel')}
          </Button>
          <Button type="submit" variant="brand" disabled={mutation.isPending}>
            {mutation.isPending ? t('employees.saving') : t('employees.save')}
          </Button>
        </>
      }
    >
      <Form {...form}>
        {/* Identity-mag — MINDKÉT módban SZERKESZTHETŐ (az e-mail a kulcs; edit → Odin identity-core PATCH,
            e-mail-ütközés → az e-mail mezőre vezetett hiba). Create-nél match esetén csak hozzárendelés. */}
        <TextField<EmployeeFormValues>
          name="email"
          label={t('employees.fields.email')}
          hint={t('employees.fields.emailHint')}
          required
          type="email"
          autoComplete="off"
        />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TextField<EmployeeFormValues>
            name="familyName"
            label={t('employees.fields.familyName')}
            required
            autoComplete="off"
          />
          <TextField<EmployeeFormValues>
            name="givenName"
            label={t('employees.fields.givenName')}
            required
            autoComplete="off"
          />
        </div>

        {/* Becenév + telefon EGY SORBAN, ebben a sorrendben (user-döntés; a kapcsolattartó-űrlap ugyanígy). A
            becenév a `platform.identity_profiles.nickname` (dirty-alapon mentve, nem nulláz némán); a telefon a
            Profilommal KÖZÖS `platform.identity_profiles.phone`. Mobilon egymás alá törnek, mint a név-pár. */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TextField<EmployeeFormValues>
            name="nickname"
            label={t('employees.fields.nickname')}
            hint={t('employees.fields.nicknameHint')}
            autoComplete="off"
          />
          <TextField<EmployeeFormValues>
            name="phone"
            label={t('employees.fields.phone')}
            type="tel"
            autoComplete="off"
          />
        </div>

        <SearchableSelectField<EmployeeFormValues>
          name="positionId"
          label={t('employees.fields.position')}
          placeholder={t('employees.fields.positionPlaceholder')}
          options={positionOptions}
          required
        />

        {/* FOGLALKOZTATÁSI FORMA + MUNKÁLTATÓ (user-döntés, HR-kör #43) — a beosztás ALATT, egymás mellett; alapból
            alkalmazott / MORGENS Design Kft. A munkáltató kulcsa a számlázási kiállítóké (MOR / MDIG). */}
        <div className="grid gap-3 sm:grid-cols-2">
          <SelectField<EmployeeFormValues>
            name="employmentType"
            label={t('employees.fields.employmentType')}
            options={EMPLOYMENT_TYPES.map((v) => ({
              value: v,
              label: t(`employees.employmentTypes.${v}`),
            }))}
          />
          <SelectField<EmployeeFormValues>
            name="employerKey"
            label={t('employees.fields.employer')}
            options={EMPLOYER_KEYS.map((v) => ({ value: v, label: t(`employees.employers.${v}`) }))}
          />
        </div>

        {/* PRÓBAIDŐ (user-döntés, #111) — a foglalkoztatás ALATT: kapcsoló, és bekapcsolva alatta egy sorban a
            kezdet + lejárat (egyik sem kötelező). Kikapcsoláskor a dátumok a mentéskor törlődnek (`probationBody`). */}
        <SwitchField<EmployeeFormValues> name="probation" label={t('employees.fields.probation')} />
        {probation && (
          <div className="grid gap-3 sm:grid-cols-2">
            <DateField<EmployeeFormValues>
              name="probationStartDate"
              label={t('employees.fields.probationStartDate')}
            />
            <DateField<EmployeeFormValues>
              name="probationEndDate"
              label={t('employees.fields.probationEndDate')}
            />
          </div>
        )}

        {/* MUNKAVISZONY ÉS SZEMÉLYES ADATOK a beosztás ALATT (user-döntés, HR-kör #40): [Munkába lépés | Kilépés
            napja], alatta [Születésnap | Névnap], alatta a gyerekek (többet is lehet). Minden dátum nap-pontos (a
            globális `DatePicker` `YYYY-MM-DD` szerződése) → nincs zóna-kezelés. Üresen hagyva `null` (törlés).
            A születésnapot, a névnapot és a gyerekeket a dolgozó a belső Profilomon maga is írhatja. */}
        <div className="grid gap-3 sm:grid-cols-2">
          <DateField<EmployeeFormValues>
            name="employmentStartDate"
            label={t('employees.fields.employmentStartDate')}
          />
          <DateField<EmployeeFormValues>
            name="employmentEndDate"
            label={t('employees.fields.employmentEndDate')}
          />
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <DateField<EmployeeFormValues> name="birthDate" label={t('employees.fields.birthDate')} />
          <DateField<EmployeeFormValues> name="nameDay" label={t('employees.fields.nameDay')} />
        </div>
        <PersonChildrenField<EmployeeFormValues> name="children" />

        {/* KEZDŐ JELSZÓ — CSAK a FELVÉTELNÉL, és OPCIONÁLIS. Üresen hagyva a belépést a köszöntő-levél
            egyszer használatos, lejáró LINKJE adja (ez a biztonságosabb, alapértelmezett út); kitöltve a
            kolléga azonnal be tud lépni. A tartalmi policy autoritása a SZERVER — a mező alatti blokk a
            szabályt tükrözi, és a 422 gépi kódjait lokalizálva mutatja. SZERKESZTÉSNÉL nincs ilyen mező. */}
        {isCreate && (
          <div className="space-y-1">
            <TextField<EmployeeFormValues>
              name="password"
              label={t('employees.fields.password')}
              hint={t('employees.fields.passwordHint')}
              type="password"
              autoComplete="new-password"
            />
            <PasswordPolicyNote codes={passwordCodes} />
          </div>
        )}
      </Form>
    </FormSheet>
  );
}
